<script setup lang="ts">
import { nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { chipStyle } from '@/todos/tags'
import TagNameInput from './TagNameInput.vue'

// The further tags a tag page is narrowed by (every todo shown carries all of them): a chip with an ✕ for each, and a ＋
// that opens a tag field with the suggestions of the tags still available to add.
const props = defineProps<{
  tags: string[]
  available: string[]
  colorFor: (name: string) => string | null
}>()
const emit = defineEmits<{ add: [tag: string]; remove: [tag: string] }>()
const { t } = useI18n()
const picking = ref(false)
const draft = ref('')
const field = ref<InstanceType<typeof TagNameInput> | null>(null)

async function open(): Promise<void> {
  picking.value = !picking.value
  draft.value = ''
  if (picking.value) {
    await nextTick()
    field.value?.focus()
  }
}

function close(): void {
  picking.value = false
  draft.value = ''
}

function pick(tag: string): void {
  close()
  emit('add', tag)
}

// Enter on typed text takes the tag that is spelled exactly like it (any case), else nothing.
function submit(): void {
  const typed = draft.value.trim().replace(/^#+/, '').toLocaleLowerCase()
  const found = props.available.find((tag) => tag.toLocaleLowerCase() === typed)
  if (found) pick(found)
}
</script>

<template>
  <div class="filter-bar" data-testid="tag-filter">
    <span
      v-for="tag in tags"
      :key="tag"
      class="ui-chip filter-chip"
      :style="chipStyle(colorFor(tag))"
    >
      # {{ tag }}
      <button
        type="button"
        class="remove"
        :aria-label="t('list.removeFilterTag', { tag })"
        :title="t('list.removeFilterTag', { tag })"
        @click="emit('remove', tag)"
      >
        ✕
      </button>
    </span>
    <span v-if="available.length" class="picker">
      <button
        type="button"
        class="ui-btn ui-btn-icon add"
        :aria-expanded="picking"
        :aria-label="t('list.addFilterTag')"
        :title="t('list.addFilterTag')"
        @click="open"
      >
        ＋
      </button>
      <TagNameInput
        v-if="picking"
        ref="field"
        v-model="draft"
        class="field"
        :names="available"
        :placeholder="t('list.filterTagPlaceholder')"
        :aria-label="t('list.addFilterTag')"
        @pick="pick"
        @submit="submit"
        @blur="close"
        @escape="close"
      />
    </span>
  </div>
</template>

<style scoped>
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  margin: 0 0 0.5rem;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.remove {
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
  padding: 0.25rem 0.35rem;
  min-width: 1.75rem;
  min-height: 1.75rem;
}

.picker {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.field {
  width: 12rem;
}
</style>
